// src/components/YellowLine.jsx
import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import { YELLOW_STATIONS } from "../stations/yellowStations";
import yellowData from "../stations/yellowSchedule.json";

export default function YellowLine({
  map,
  theme = "light",
  showStations = true,
  fitOnMount = false,
  animateTrains = true,
  timeOfDayOverride,
}) {
  const timerRef = useRef(null);
  const tripDefsRef = useRef([]);
  const liveMarkersRef = useRef(new Map());
  const popupRef = useRef(null);
  const didFitRef = useRef(false);

  useEffect(() => {
    if (!map) return;
    const isDark = theme === "dark";

    const stations = (YELLOW_STATIONS || []).filter(
      (s) => typeof s.lat === "number" && typeof s.lng === "number"
    );
    const points = stations.map(({ lat, lng }) => [lng, lat]);
    if (points.length < 2) return;

    const removeLayersAndSources = () => {
      ["yellow-line-layer", "yellow-stations-layer", "yellow-stations-labels"].forEach((id) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      ["yellow-line", "yellow-stations"].forEach((id) => {
        if (map.getSource(id)) map.removeSource(id);
      });
    };

    const ensureOrder = () => {
      if (map.getLayer("place-major")) {
        ["yellow-stations-labels", "yellow-stations-layer", "yellow-line-layer"].forEach((id) => {
          if (map.getLayer(id)) {
            try {
              map.moveLayer(id, "place-major");
            } catch { }
          }
        });
      }
    };

    const addLayers = () => {
      if (!map.isStyleLoaded()) return;
      removeLayersAndSources();

      if (fitOnMount && !didFitRef.current) {
        const b = points.reduce(
          (acc, p) => acc.extend(p),
          new maplibregl.LngLatBounds(points[0], points[0])
        );
        map.fitBounds(b, { padding: 40, duration: 300 });
        didFitRef.current = true;
      }

      map.addSource("yellow-line", {
        type: "geojson",
        data: { type: "Feature", geometry: { type: "LineString", coordinates: points } },
      });
      map.addLayer({
        id: "yellow-line-layer",
        type: "line",
        source: "yellow-line",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#EAB308", "line-width": 4, "line-opacity": 0.95 },
      });

      if (showStations) {
        map.addSource("yellow-stations", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: stations.map((s) => ({
              type: "Feature",
              properties: { name: s.name },
              geometry: { type: "Point", coordinates: [s.lng, s.lat] },
            })),
          },
        });

        map.addLayer({
          id: "yellow-stations-layer",
          type: "circle",
          source: "yellow-stations",
          paint: {
            "circle-radius": 8,
            "circle-color": "#EAB308",
            "circle-stroke-color": isDark ? "#000" : "#fff",
            "circle-stroke-width": 2.5,
            "circle-opacity": 1,
          },
        });

        map.addLayer({
          id: "yellow-stations-labels",
          type: "symbol",
          source: "yellow-stations",
          minzoom: 10, // show labels only when zoomed in enough
          layout: {
            "text-field": ["get", "name"],
            "text-font": ["Noto Sans Regular"],
            // step sizes prevent sub-pixel anti-alias blur
            "text-size": [
              "step",
              ["zoom"],
              11,  // default
              13, 12,
              15, 13,
              17, 14
            ],
            "text-variable-anchor": ["top", "bottom", "left", "right"],
            "text-radial-offset": 0.6,
            "text-padding": 4,
            "text-anchor": "top",
            "text-allow-overlap": false,
            "text-optional": true,
            "text-pitch-alignment": "map",
            "text-rotation-alignment": "map",
            "icon-allow-overlap": false
          },
          paint: {
            "text-color": isDark ? "#ffffff" : "#111111",
            "text-halo-color": isDark ? "rgba(0,0,0,0.8)" : "rgba(255,255,255,0.9)",
            "text-halo-width": 1,
            "text-halo-blur": 0,
          }
        });

        map.on("click", "yellow-stations-layer", (e) => {
          const feature = e.features[0];
          const coordinates = feature.geometry.coordinates.slice();
          const stationName = feature.properties.name;

          if (popupRef.current) popupRef.current.remove();

          const station = stations.find((s) => s.name === stationName);
          if (!station) return;
          const stationId = (station.station_id || "").trim().toUpperCase();

          // ===== ETA calculation logic (from Favorites) =====
          const hhmmssToMs = (t) => {
            const [hh, mm, ss] = t.split(":").map((x) => parseInt(x, 10));
            return ((hh * 60 + mm) * 60 + (ss || 0)) * 1000;
          };
          const IST_OFFSET_MIN = 330;
          const DAY_MS = 24 * 60 * 60 * 1000;
          const nowWithinDayIST = () => {
            const n = Date.now() + IST_OFFSET_MIN * 60 * 1000;
            const d = new Date(n);
            return (
              ((d.getUTCHours() * 60 + d.getUTCMinutes()) * 60 + d.getUTCSeconds()) * 1000 +
              d.getUTCMilliseconds()
            );
          };
          const nextEtaMinutes = (times) => {
            if (!times || !times.length) return null;
            const nowMs = nowWithinDayIST();
            const list = times
              .map((t) => (typeof t === "string" ? t : t.time))
              .filter(Boolean)
              .map(hhmmssToMs)
              .sort((a, b) => a - b);
            const idx = list.findIndex((ms) => ms >= nowMs);
            const target = idx >= 0 ? list[idx] : list[0] + DAY_MS;
            const etaMs = target - nowMs;
            return Math.max(0, Math.ceil(etaMs / 60000));
          };

          const upTrips = yellowData?.["UP Line"]?.[stationId]?.trips || [];
          const downTrips = yellowData?.["DN Line"]?.[stationId]?.trips || [];
          const toLast = nextEtaMinutes(upTrips);
          const toFirst = nextEtaMinutes(downTrips);

          const firstStation = YELLOW_STATIONS[0];
          const lastStation = YELLOW_STATIONS[YELLOW_STATIONS.length - 1];

          const etaLabel = (mins) => {
            if (mins == null) return "--";
            if (mins < 1) return "Arriving";
            if (mins < 60) return `${mins} min`;
            const hrs = Math.floor(mins / 60);
            const rem = mins % 60;
            return rem === 0 ? `${hrs} hr` : `${hrs} hr ${rem} min`;
          };

          // 🧭 Position popup properly
          const mapCanvas = map.getCanvas();
          const rect = mapCanvas.getBoundingClientRect();
          const x = e.point.x;
          const y = e.point.y;
          let anchor = "bottom";
          if (y < rect.height * 0.3) anchor = "top";
          else if (y > rect.height * 0.7) anchor = "bottom";
          if (x < rect.width * 0.3) anchor += "-left";
          else if (x > rect.width * 0.7) anchor += "-right";

          // ===== Build Popup HTML =====
          const popup = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 25,
            anchor,
            maxWidth: "none",
          })
            .setLngLat(coordinates)
            .setHTML(`
  <div class="custom-popup-card" style="
    font-family: Inter, sans-serif;
    background: ${isDark ? "#1f1f1f" : "#ffffff"};
    color: ${isDark ? "#f9f9f9" : "#111"};
    border-radius: 14px;
    box-shadow: 0 6px 18px rgba(0,0,0,0.25);
    border: ${isDark ? "1px solid #333" : "1px solid #ddd"};
    width: auto;
    min-width: 200px;
    max-width: 280px;
    padding: 14px 16px;
    animation: popupFadeIn 0.2s ease-out;
  ">

    <div style="font-weight:700;font-size:15px;margin-bottom:10px;">
      ${stationName}
    </div>

    <div style="display:flex;justify-content:space-between;font-weight:600;font-size:13px;margin-bottom:6px;">
      <span>Towards</span>
      <span>ETA</span>
    </div>

    <!-- First direction -->
    <div style="display:flex;justify-content:space-between;gap:8px;font-size:13px;margin-bottom:4px;">
      <span style="flex:1;">${firstStation.name}</span>
      <span style="font-weight:600;white-space:nowrap;">${etaLabel(toFirst)}</span>
    </div>

    <!-- Second direction -->
    <div style="display:flex;justify-content:space-between;gap:8px;font-size:13px;margin-bottom:10px;">
      <span style="flex:1;">${lastStation.name}</span>
      <span style="font-weight:600;white-space:nowrap;">${etaLabel(toLast)}</span>
    </div>

    ${ (station.parking?.twoWheeler > 0 || station.parking?.fourWheeler > 0)
      ? `
        <hr style="border:none;border-top:1px solid ${isDark ? "#333" : "#e3e3e3"};margin:10px 0;">

        <div style="display:flex;justify-content:space-between;font-weight:600;font-size:13px;margin-bottom:6px;">
          <span>Parking</span>
          <span>Total Spots</span>
        </div>

        ${station.parking?.twoWheeler > 0
          ? `<div style="display:flex;justify-content:space-between;font-size:13px;padding:2px 0;">
                <span>Two Wheeler</span>
                <span>${station.parking.twoWheeler}</span>
             </div>` : ""
        }

        ${station.parking?.fourWheeler > 0
          ? `<div style="display:flex;justify-content:space-between;font-size:13px;padding:2px 0;">
                <span>Four Wheeler</span>
                <span>${station.parking.fourWheeler}</span>
             </div>` : ""
        }
      `
      : ""
    }

  </div>

  <style>
    @keyframes popupFadeIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
  </style>
`)
            .addTo(map);

          const popupEl = popup.getElement();
          const contentEl = popupEl.querySelector(".maplibregl-popup-content");
          if (contentEl) contentEl.replaceWith(...contentEl.childNodes);
          popupRef.current = popup;
          window.currentYellowPopup = popup;
        });


        // Close popup when clicking elsewhere
        map.on("click", (e) => {
          const features = map.queryRenderedFeatures(e.point, {
            layers: ["yellow-stations-layer"],
          });
          if (!features.length && popupRef.current) {
            popupRef.current.remove();
            popupRef.current = null;
          }
        });
      }

      ensureOrder();
    };

    const onLoad = () => addLayers();
    const onStyleLoad = () => addLayers();
    const onIdle = () => ensureOrder();

    if (map.isStyleLoaded()) addLayers();
    map.on("load", onLoad);
    map.once("style.load", () => setTimeout(() => addLayers(), 300));
    map.on("idle", onIdle);

    // ===== Train animation (unchanged) =====
    if (animateTrains) {
      const DAY_MS = 24 * 60 * 60 * 1000;
      const IST_OFFSET_MS = 330 * 60 * 1000;
      const istNowMs = () => Date.now() + IST_OFFSET_MS;

      const hhmmssToMs = (t) => {
        const [hh, mm, ss] = t.split(":").map((x) => parseInt(x, 10));
        return ((hh * 60 + mm) * 60 + (ss || 0)) * 1000;
      };
      const istMidnightEpoch = () => {
        const n = istNowMs();
        const d = new Date(n);
        const sinceMidnight =
          ((d.getUTCHours() * 60 + d.getUTCMinutes()) * 60 + d.getUTCSeconds()) * 1000 +
          d.getUTCMilliseconds();
        return n - sinceMidnight;
      };
      const nowISTms = () => {
        const base = istMidnightEpoch();
        return timeOfDayOverride ? base + hhmmssToMs(timeOfDayOverride) : istNowMs();
      };

      const dirMap = { UP: yellowData?.["UP Line"] || {}, DOWN: yellowData?.["DN Line"] || {} };
      const stationIdToIndex = new Map();
      stations.forEach((s, idx) =>
        stationIdToIndex.set((s.station_id || "").trim().toUpperCase(), idx)
      );

      const buildTrips = () => {
        const midnight = istMidnightEpoch();
        const allTrips = [];

        for (const dir of ["UP", "DOWN"]) {
          const line = dirMap[dir];
          const tripMap = new Map();

          for (const [stationId, stationObj] of Object.entries(line)) {
            const trips = stationObj?.trips || [];
            const idx = stationIdToIndex.get((stationId || "").trim().toUpperCase());
            if (idx === undefined) continue;
            for (const t of trips) {
              const key = `${dir}_${t.tripNumber}_${t.trainId}`;
              const arr = tripMap.get(key) || [];
              arr.push({ idx, time: t.time });
              tripMap.set(key, arr);
            }
          }

          for (const [key, raw] of tripMap.entries()) {
            const ordered = raw
              .slice()
              .sort((a, b) => (dir === "UP" ? a.idx - b.idx : b.idx - a.idx));

            let dayOffset = 0;
            let prevWithin = null;
            const events = ordered.map(({ idx, time }) => {
              const within = hhmmssToMs(time);
              if (prevWithin !== null && within < prevWithin) dayOffset += DAY_MS;
              const abs = midnight + dayOffset + within;
              prevWithin = within;
              return { idx, abs };
            });

            if (events.length >= 2) {
              const legs = [];
              for (let i = 0; i < events.length; i++) {
                const curr = events[i];
                const next = events[i + 1];

                if (!next) {
                  legs.push({ i1: curr.idx, i2: curr.idx, t1: curr.abs, t2: curr.abs + 15000 });
                  break;
                }
                if (Math.abs(next.idx - curr.idx) !== 1 || next.abs <= curr.abs) continue;

                const depart = curr.abs + 15000;
                const arrive = next.abs;

                if (arrive > depart) {
                  legs.push({ i1: curr.idx, i2: curr.idx, t1: curr.abs, t2: depart });
                  legs.push({ i1: curr.idx, i2: next.idx, t1: depart, t2: arrive });
                } else {
                  legs.push({ i1: curr.idx, i2: next.idx, t1: curr.abs, t2: arrive });
                }
              }
              if (legs.length) allTrips.push({ key, legs });
            }
          }
        }
        return allTrips;
      };

      tripDefsRef.current = buildTrips();

      const lerp = (a, b, t) => a + (b - a) * t;
      const lerpPos = (p1, p2, t) => [lerp(p1[0], p2[0], t), lerp(p1[1], p2[1], t)];
      const ensureMarker = (key) => {
        let marker = liveMarkersRef.current.get(key);
        if (!marker) {
          const el = document.createElement("img");
          el.src = "/yellow-metro.jpg";
          el.style.width = "28px";
          el.style.height = "28px";
          el.style.objectFit = "contain";
          el.style.borderRadius = "50%";
          el.style.boxShadow = "0 0 4px rgba(0,0,0,0.4)";
          marker = new maplibregl.Marker({ element: el, anchor: "center" })
            .setLngLat(points[0])
            .addTo(map);
          liveMarkersRef.current.set(key, marker);
        }
        return marker;
      };

      const UPDATE_INTERVAL = 5000;
      const step = () => {
        const now = nowISTms();

        for (const trip of tripDefsRef.current) {
          const firstLeg = trip.legs[0];
          const lastLeg = trip.legs[trip.legs.length - 1];

          if (now < firstLeg.t1 || now > lastLeg.t2) {
            const m = liveMarkersRef.current.get(trip.key);
            if (m) {
              m.remove();
              liveMarkersRef.current.delete(trip.key);
            }
            continue;
          }

          let moved = false;
          for (const leg of trip.legs) {
            if (now >= leg.t1 && now <= leg.t2) {
              const p1 = points[leg.i1];
              const p2 = points[leg.i2];
              const total = leg.t2 - leg.t1;
              const elapsed = now - leg.t1;
              const t = Math.min(Math.max(elapsed / total, 0), 1);
              ensureMarker(trip.key).setLngLat(lerpPos(p1, p2, t));
              moved = true;
              break;
            }
          }

          if (!moved) {
            const m = liveMarkersRef.current.get(trip.key);
            if (m) {
              m.remove();
              liveMarkersRef.current.delete(trip.key);
            }
          }
        }

        timerRef.current = setTimeout(step, UPDATE_INTERVAL);
      };
      step();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      for (const m of liveMarkersRef.current.values()) m.remove();
      liveMarkersRef.current.clear();

      if (popupRef.current) popupRef.current.remove();

      map.off("load", onLoad);
      map.off("style.load", onStyleLoad);
      map.off("idle", onIdle);
      if (map.isStyleLoaded()) removeLayersAndSources();
    };
  }, [map, theme, showStations, fitOnMount, animateTrains, timeOfDayOverride]);

  return null;
}
